import { useEffect, useRef } from "react";
import { CookieBanner } from "@/components/CookieBanner";
import { FeedbackButton } from "@/components/FeedbackButton";
import { metaFor, applyMeta } from "@/lib/seo";
import { trackPage } from "@/lib/consent";
import { Privacy, Terms, Cookies } from "@/pages/public/Legal";
import { NotFound, ThankYou } from "@/pages/public/StatusPages";
import WhatYouNeed from "@/pages/public/WhatYouNeed";
import Billing from "@/pages/app/Billing";
import TestCheckout from "@/pages/app/TestCheckout";
import Westmont from "@/pages/app/Westmont";
import Ideation from "@/pages/app/Ideation";
import WestmontPublic from "@/pages/public/WestmontPublic";
import { PaymentSuccess, PaymentCancel } from "@/pages/PaymentResult";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { api } from "@/lib/api";
import PublicLayout from "@/components/PublicLayout";
import AppLayout from "@/components/AppLayout";
import Home from "@/pages/public/Home";
import { HowItWorks, IPProtection, ExpertSupport, FAQs } from "@/pages/public/InfoPages";
import PublicFunding, { PublicOpportunity } from "@/pages/public/PublicFunding";
import Pricing from "@/pages/public/Pricing";
import Contact from "@/pages/public/Contact";
import Login from "@/pages/public/Login";
import MfaVerify from "@/pages/app/MfaVerify";
import Dashboard from "@/pages/app/Dashboard";
import Onboarding from "@/pages/app/Onboarding";
import Business from "@/pages/app/Business";
import Projects from "@/pages/app/Projects";
import ProjectDetail from "@/pages/app/ProjectDetail";
import Funding from "@/pages/app/Funding";
import OpportunityDetail from "@/pages/app/OpportunityDetail";
import Applications from "@/pages/app/Applications";
import ApplicationDetail from "@/pages/app/ApplicationDetail";
import IPSearch from "@/pages/app/IPSearch";
import Alerts from "@/pages/app/Alerts";
import Security from "@/pages/app/Security";
import Admin from "@/pages/app/Admin";
import Programme from "@/pages/app/Programme";
import HubDocuments from "@/pages/app/HubDocuments";
import { PanelList, PanelDetail } from "@/pages/app/Panels";

const Loading = () => <div data-testid="loading-screen" className="grid min-h-screen place-items-center text-sm text-slate-500">Loading…</div>;

const AuthCallback = () => {
  const navigate = useNavigate();
  const { refresh } = useAuth();
  const done = useRef(false);
  useEffect(() => {
    if (done.current) return;
    done.current = true;
    const sid = new URLSearchParams(window.location.hash.slice(1)).get("session_id");
    api.post("/auth/session", { session_id: sid }).then(async ({ data }) => {
      window.history.replaceState(null, "", window.location.pathname);
      await refresh();
      navigate(data.mfa_pending ? "/mfa" : "/app", { replace: true });
    }).catch(() => navigate("/login?error=1", { replace: true }));
  }, [navigate, refresh]);
  return <Loading />;
};

const Protected = ({ children, admin }) => {
  const { user, loading, orgs } = useAuth();
  const loc = useLocation();
  if (loading) return <Loading />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.mfa_pending) return <Navigate to="/mfa" replace />;
  if (admin && !user.is_admin) return <Navigate to="/app" replace />;
  if (!orgs.length && !loc.pathname.startsWith("/app/onboarding") && !loc.pathname.startsWith("/app/panels") && !admin && !user.is_admin) return <Navigate to="/app/onboarding" replace />;
  return <AppLayout>{children}</AppLayout>;
};

const pub = (el) => <PublicLayout>{el}</PublicLayout>;
const prot = (el, admin) => <Protected admin={admin}>{el}</Protected>;

const useRouteMeta = () => {
  const { pathname } = useLocation();
  const { settings } = useAuth();
  useEffect(() => {
    const title = applyMeta(metaFor(pathname), settings.platform_name, pathname);
    trackPage(pathname, title);
  }, [pathname, settings.platform_name]);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);
};

function AppRouter() {
  const location = useLocation();
  useRouteMeta();
  if (location.hash?.includes("session_id=")) return <AuthCallback />;
  return (
    <Routes>
      <Route path="/" element={pub(<Home />)} />
      <Route path="/how-it-works" element={pub(<HowItWorks />)} />
      <Route path="/ip-protection" element={pub(<IPProtection />)} />
      <Route path="/grants-funding" element={pub(<PublicFunding />)} />
      <Route path="/grants-funding/:id" element={pub(<PublicOpportunity />)} />
      <Route path="/pricing" element={pub(<Pricing />)} />
      <Route path="/expert-support" element={pub(<ExpertSupport />)} />
      <Route path="/faqs" element={pub(<FAQs />)} />
      <Route path="/contact" element={pub(<Contact />)} />
      <Route path="/login" element={pub(<Login />)} />
      <Route path="/thank-you" element={pub(<ThankYou />)} />
      <Route path="/what-you-need" element={pub(<WhatYouNeed />)} />
      <Route path="/privacy" element={pub(<Privacy />)} />
      <Route path="/terms" element={pub(<Terms />)} />
      <Route path="/cookies" element={pub(<Cookies />)} />
      <Route path="/mfa" element={<MfaVerify />} />
      <Route path="/app" element={prot(<Dashboard />)} />
      <Route path="/app/onboarding" element={prot(<Onboarding />)} />
      <Route path="/app/business" element={prot(<Business />)} />
      <Route path="/app/billing" element={prot(<Billing />)} />
      <Route path="/app/billing/test-checkout" element={prot(<TestCheckout />)} />
      <Route path="/app/westmont" element={prot(<Westmont />)} />
      <Route path="/app/ideation" element={prot(<Ideation />)} />
      <Route path="/app/programme" element={prot(<Programme />)} />
      <Route path="/app/hub-documents" element={prot(<HubDocuments />)} />
      <Route path="/app/panels" element={prot(<PanelList />)} />
      <Route path="/app/panels/:id" element={prot(<PanelDetail />)} />
      <Route path="/westmont" element={pub(<WestmontPublic />)} />
      <Route path="/payment/success" element={prot(<PaymentSuccess />)} />
      <Route path="/payment/cancel" element={prot(<PaymentCancel />)} />
      <Route path="/app/projects" element={prot(<Projects />)} />
      <Route path="/app/projects/:id" element={prot(<ProjectDetail />)} />
      <Route path="/app/funding" element={prot(<Funding />)} />
      <Route path="/app/funding/:id" element={prot(<OpportunityDetail />)} />
      <Route path="/app/applications" element={prot(<Applications />)} />
      <Route path="/app/applications/:id" element={prot(<ApplicationDetail />)} />
      <Route path="/app/ip-search" element={prot(<IPSearch />)} />
      <Route path="/app/alerts" element={prot(<Alerts />)} />
      <Route path="/app/security" element={prot(<Security />)} />
      <Route path="/app/admin" element={prot(<Admin />, true)} />
      <Route path="*" element={pub(<NotFound />)} />
    </Routes>
  );
}

export default function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AuthProvider>
          <AppRouter />
          <CookieBanner />
          <FeedbackButton />
          <Toaster richColors position="top-right" />
        </AuthProvider>
      </BrowserRouter>
    </div>
  );
}
